import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  DollarSign, 
  AlertTriangle, 
  CheckCircle, 
  Loader2,
  RefreshCw,
  CreditCard,
  Receipt
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

// Refund reason templates
const REFUND_TEMPLATES = {
  service_not_delivered: "Service was not delivered as scheduled",
  customer_dissatisfaction: "Customer was not satisfied with the service",
  duplicate_charge: "Customer was charged twice for the same service",
  appointment_cancelled: "Appointment was cancelled by the stylist",
  scheduling_error: "Booking error - time slot was not available",
  medical_emergency: "Customer had a medical emergency",
  stylist_no_show: "Stylist was unavailable/no show",
  weather_closure: "Salon closed due to weather conditions",
  partial_service: "Only partial service was completed",
  price_adjustment: "Price adjustment for service",
  other: "Other reason (see notes)"
};

const RefundModal = ({ 
  isOpen, 
  onClose, 
  appointment, 
  token,
  onRefundComplete 
}) => {
  const [refundAmount, setRefundAmount] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [notes, setNotes] = useState("");
  const [isFullRefund, setIsFullRefund] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [refundResult, setRefundResult] = useState(null);

  const originalAmount = appointment?.total_amount || appointment?.total || appointment?.price || 0;
  const clientName = appointment?.client?.name || appointment?.guest_name || "Customer";
  const serviceName = appointment?.service?.name || appointment?.service_name || "Service";

  const handleTemplateChange = (template) => {
    setSelectedTemplate(template);
    if (template !== "other") {
      setCustomReason(REFUND_TEMPLATES[template]);
    } else {
      setCustomReason("");
    }
  };

  const handleRefund = async () => {
    if (!selectedTemplate) {
      toast.error("Please select a refund reason");
      return;
    }

    const reason = selectedTemplate === "other" ? customReason : REFUND_TEMPLATES[selectedTemplate];
    if (!reason.trim()) {
      toast.error("Please provide a refund reason");
      return;
    }

    const amount = isFullRefund ? null : parseFloat(refundAmount);
    if (!isFullRefund && (!amount || amount <= 0 || amount > originalAmount)) {
      toast.error(`Please enter a valid amount between $0.01 and $${originalAmount.toFixed(2)}`);
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/appointments/${appointment.appointment_id}/refund`,
        {
          appointment_id: appointment.appointment_id,
          amount: amount,
          reason: reason,
          reason_template: selectedTemplate,
          notes: notes || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setRefundResult(response.data);
      setSuccess(true);
      toast.success(`Refund of $${response.data.refund_amount.toFixed(2)} processed successfully!`);
      
      if (onRefundComplete) {
        onRefundComplete(response.data);
      }
    } catch (error) {
      console.error("Refund error:", error);
      toast.error(error.response?.data?.detail || "Failed to process refund");
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setRefundAmount("");
    setSelectedTemplate("");
    setCustomReason("");
    setNotes("");
    setIsFullRefund(true);
    setProcessing(false);
    setSuccess(false);
    setRefundResult(null);
    onClose();
  };

  if (!appointment) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-red-500" />
            Issue Refund
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Refund Processed!</h3>
            <p className="text-gray-600 mb-4">
              ${refundResult?.refund_amount?.toFixed(2)} has been refunded to {clientName}.
            </p>
            {refundResult?.stripe_refund_id && (
              <p className="text-sm text-gray-500">
                Stripe Refund ID: {refundResult.stripe_refund_id}
              </p>
            )}
            <Button onClick={handleClose} className="mt-4">
              Close
            </Button>
          </div>
        ) : (
          <>
            {/* Appointment Details */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Customer:</span>
                <span className="font-medium">{clientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Service:</span>
                <span className="font-medium">{serviceName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Date:</span>
                <span className="font-medium">{appointment.date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Original Amount:</span>
                <span className="font-bold text-lg">${originalAmount.toFixed(2)}</span>
              </div>
              {appointment.payment_method && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment Method:</span>
                  <Badge variant="outline">
                    <CreditCard className="w-3 h-3 mr-1" />
                    {appointment.payment_method === 'stripe' ? 'Card' : appointment.payment_method}
                  </Badge>
                </div>
              )}
            </div>

            {/* Refund Amount */}
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant={isFullRefund ? "default" : "outline"}
                  className={isFullRefund ? "bg-purple-500" : ""}
                  onClick={() => setIsFullRefund(true)}
                >
                  Full Refund
                </Button>
                <Button
                  type="button"
                  variant={!isFullRefund ? "default" : "outline"}
                  className={!isFullRefund ? "bg-purple-500" : ""}
                  onClick={() => setIsFullRefund(false)}
                >
                  Partial Refund
                </Button>
              </div>

              {!isFullRefund && (
                <div>
                  <Label>Refund Amount</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={originalAmount}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      className="pl-8"
                      placeholder={`Max: ${originalAmount.toFixed(2)}`}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Refund Reason */}
            <div className="space-y-3">
              <div>
                <Label>Reason for Refund *</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(REFUND_TEMPLATES).map(([key, value]) => (
                      <SelectItem key={key} value={key}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedTemplate === "other" && (
                <div>
                  <Label>Custom Reason *</Label>
                  <Input
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                    placeholder="Enter the refund reason..."
                  />
                </div>
              )}

              <div>
                <Label>Additional Notes (Optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional details about this refund..."
                  rows={2}
                />
              </div>
            </div>

            {/* Warning */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Important</p>
                  <p className="text-amber-700">
                    {appointment.payment_method === 'stripe' 
                      ? "This refund will be processed through Stripe. The customer will receive the funds in 5-10 business days."
                      : "This was a manual payment. You'll need to refund the customer directly via their original payment method."}
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={processing}>
                Cancel
              </Button>
              <Button
                onClick={handleRefund}
                disabled={processing || !selectedTemplate}
                className="bg-red-500 hover:bg-red-600"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Process Refund ${isFullRefund ? originalAmount.toFixed(2) : (refundAmount || '0.00')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RefundModal;
